<template>
  <section id="userSettings">
    <b-card
      id="userSettingsCard"
      class="blockDrawer large"
      :class="$store.state.drawer.userSettings.position"
    >
      <template v-slot:header>
        <a
          @click="closeUserSettings()"
          class="close clickable"
          title="Back"
        >
          <i class="far fa-times"></i>
        </a>
        <h4>Account settings</h4>
        <small class="us-subtitle">{{ currentUser && currentUser.email }}</small>
      </template>

      <!-- Section tabs (in-flow; the FarmDetails top-nav is absolutely
           positioned against that drawer's taller header) -->
      <div class="us-tabs">
        <ul class="nav nav-tabs">
          <li class="nav-item clickable" @click="tab = 'profile'">
            <span class="nav-link" :class="{ active: tab === 'profile' }">
              <i class="fas fa-user mr-1"></i> Profile
            </span>
          </li>
          <li class="nav-item clickable" @click="tab = 'security'">
            <span class="nav-link" :class="{ active: tab === 'security' }">
              <i class="fas fa-lock mr-1"></i> Security
            </span>
          </li>
        </ul>
      </div>

      <!-- ================= PROFILE ================= -->
      <form v-if="tab === 'profile'" @submit.prevent="updateProfile()" class="us-section">
        <div class="row">
          <div class="col">
            <label for="firstName">First name</label>
            <input v-model="profileData.firstName" type="text" placeholder="Jane" id="firstName" class="form-control" autocomplete="given-name" />
          </div>
          <div class="col">
            <label for="lastName">Last name</label>
            <input v-model="profileData.lastName" type="text" placeholder="Goodall" id="lastName" class="form-control" autocomplete="family-name" />
          </div>
        </div>
        <div class="form-group mt-2">
          <label for="company">Company or farm</label>
          <input v-model="profileData.company" type="text" placeholder="Company" id="company" class="form-control" autocomplete="organization" />
        </div>

        <label class="mt-3 mb-1"><strong>Email preferences</strong></label>
        <b-form-checkbox v-model="profileData.emailNotifications" name="email-notifications" switch>
          Account notifications
          <br><small class="text-muted">Collaboration invites, processing results, and other activity on your farms.</small>
        </b-form-checkbox>
        <b-form-checkbox v-model="profileData.emailUpdates" name="email-updates" switch class="mt-2">
          Product updates &amp; announcements
          <br><small class="text-muted">Occasional news from the Every.Farm team. Unsubscribe anytime.</small>
        </b-form-checkbox>

        <b-form-group label="Measurement system" v-slot="{ ariaDescribedby }" class="mt-3">
          <b-form-radio v-model="profileData.measurementSystem" :aria-describedby="ariaDescribedby" name="measurement-system-radios" value="Imperial">Imperial</b-form-radio>
          <b-form-radio v-model="profileData.measurementSystem" :aria-describedby="ariaDescribedby" name="measurement-system-radios" value="Metric">Metric</b-form-radio>
        </b-form-group>

        <div class="us-actions">
          <button type="submit" class="btn btn-primary" :disabled="savingProfile">
            <i v-if="savingProfile" class="fas fa-sync fa-spin mr-1"></i>{{ savingProfile ? 'Saving…' : 'Save changes' }}
          </button>
          <button type="button" class="btn btn-link text-muted" @click="closeUserSettings()">Done</button>
        </div>
      </form>

      <!-- ================= SECURITY ================= -->
      <div v-if="tab === 'security'" class="us-section">

        <!-- Accounts that don't sign in with a password can't change one here -->
        <div v-if="!hasPasswordProvider" class="alert alert-info">
          This account signs in with {{ providerLabel }}, so its password is managed there.
        </div>

        <template v-else>
          <!-- ---------- Change password ---------- -->
          <h5 class="us-h5"><i class="fas fa-key mr-1"></i> Change password</h5>
          <p class="text-muted small mb-3">
            For your security we confirm your current password first — no need to sign out and back in.
          </p>
          <form @submit.prevent="updatePassword()" novalidate>
            <!-- Hidden username field helps password managers pair the new password with the account -->
            <input type="email" :value="currentUser && currentUser.email" autocomplete="username" class="d-none" tabindex="-1" aria-hidden="true" readonly />

            <div class="form-group">
              <label for="currentPassword">Current password</label>
              <div class="us-pw">
                <input
                  id="currentPassword"
                  class="form-control"
                  :class="{ 'is-invalid': pwErrors.current }"
                  v-model="pw.current"
                  :type="showPw ? 'text' : 'password'"
                  autocomplete="current-password"
                  @input="pwErrors.current = ''"
                />
              </div>
              <div v-if="pwErrors.current" class="invalid-feedback d-block">{{ pwErrors.current }}</div>
              <a class="small clickable us-link" @click="sendResetLink()">Forgot your current password?</a>
            </div>

            <div class="form-group">
              <label for="newPassword">New password</label>
              <input
                id="newPassword"
                class="form-control"
                :class="{ 'is-invalid': pwErrors.next }"
                v-model="pw.next"
                :type="showPw ? 'text' : 'password'"
                autocomplete="new-password"
                @input="pwErrors.next = ''; pwErrors.confirm = ''"
              />
              <small class="form-text" :class="pw.next && pw.next.length < MIN_PW ? 'text-danger' : 'text-muted'">
                At least {{ MIN_PW }} characters<span v-if="pw.next"> · {{ pw.next.length }}/{{ MIN_PW }}</span>
              </small>
              <div v-if="pwErrors.next" class="invalid-feedback d-block">{{ pwErrors.next }}</div>
            </div>

            <div class="form-group">
              <label for="confirmPassword">Confirm new password</label>
              <input
                id="confirmPassword"
                class="form-control"
                :class="{ 'is-invalid': pwErrors.confirm, 'is-valid': pw.confirm && pw.confirm === pw.next && pw.next.length >= MIN_PW }"
                v-model="pw.confirm"
                :type="showPw ? 'text' : 'password'"
                autocomplete="new-password"
                @input="pwErrors.confirm = ''"
              />
              <div v-if="pwErrors.confirm" class="invalid-feedback d-block">{{ pwErrors.confirm }}</div>
            </div>

            <b-form-checkbox v-model="showPw" switch class="mb-3 small">Show passwords</b-form-checkbox>

            <div class="us-actions">
              <button type="submit" class="btn btn-primary" :disabled="savingPassword">
                <i v-if="savingPassword" class="fas fa-sync fa-spin mr-1"></i>{{ savingPassword ? 'Updating…' : 'Update password' }}
              </button>
            </div>
          </form>

          <hr class="my-4">

          <!-- ---------- Change email ---------- -->
          <h5 class="us-h5"><i class="fas fa-envelope mr-1"></i> Email address</h5>
          <p class="mb-2"><strong>{{ currentUser && currentUser.email }}</strong></p>
          <button v-if="!showEmailForm" type="button" class="btn btn-outline-secondary btn-sm" @click="showEmailForm = true">
            Change email address
          </button>
          <form v-else @submit.prevent="updateEmail()" novalidate class="mt-2">
            <div class="form-group">
              <label for="newEmail">New email address</label>
              <input
                id="newEmail"
                class="form-control"
                :class="{ 'is-invalid': emailErrors.email }"
                v-model.trim="em.next"
                type="email"
                autocomplete="email"
                @input="emailErrors.email = ''"
              />
              <div v-if="emailErrors.email" class="invalid-feedback d-block">{{ emailErrors.email }}</div>
            </div>
            <div class="form-group">
              <label for="emailPassword">Current password <small class="text-muted">(to confirm it's you)</small></label>
              <input
                id="emailPassword"
                class="form-control"
                :class="{ 'is-invalid': emailErrors.password }"
                v-model="em.password"
                type="password"
                autocomplete="current-password"
                @input="emailErrors.password = ''"
              />
              <div v-if="emailErrors.password" class="invalid-feedback d-block">{{ emailErrors.password }}</div>
            </div>
            <div class="us-actions">
              <button type="submit" class="btn btn-primary" :disabled="savingEmail">
                <i v-if="savingEmail" class="fas fa-sync fa-spin mr-1"></i>{{ savingEmail ? 'Updating…' : 'Update email' }}
              </button>
              <button type="button" class="btn btn-link text-muted" @click="cancelEmailForm()">Cancel</button>
            </div>
          </form>
        </template>
      </div>
    </b-card>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import firebase from 'firebase/compat/app'
const fb = require('../../firebaseConfig.js')

// Firebase's floor is 6; we hold the same line as signup so the two never disagree.
const MIN_PW = 6

export default {
  data () {
    return {
      MIN_PW,
      tab: 'profile',
      profileData: {
        firstName: '',
        lastName: '',
        company: '',
        emailNotifications: true,
        emailUpdates: true,
        measurementSystem: 'Imperial'
      },
      savingProfile: false,
      // Password change
      pw: { current: '', next: '', confirm: '' },
      pwErrors: { current: '', next: '', confirm: '' },
      showPw: false,
      savingPassword: false,
      // Email change
      showEmailForm: false,
      em: { next: '', password: '' },
      emailErrors: { email: '', password: '' },
      savingEmail: false
    }
  },
  computed: {
    ...mapState(['userProfile', 'currentUser']),
    // Whether this account can sign in with an email + password at all.
    hasPasswordProvider () {
      const u = fb.auth.currentUser
      if (!u || !u.providerData || !u.providerData.length) return true
      return u.providerData.some(p => p && p.providerId === 'password')
    },
    providerLabel () {
      const u = fb.auth.currentUser
      const p = u && u.providerData && u.providerData[0]
      if (!p) return 'another provider'
      return ({ 'google.com': 'Google', 'apple.com': 'Apple', 'microsoft.com': 'Microsoft' })[p.providerId] || p.providerId
    }
  },
  created () {
    // Work on a COPY — binding v-model straight to the store object meant
    // half-typed edits leaked into app state even when the user backed out.
    this.profileData = { ...this.profileData, ...(this.userProfile || {}) }
    if (this.profileData.emailUpdates === undefined) this.profileData.emailUpdates = true
  },
  methods: {
    closeUserSettings () {
      this.$store.commit('hideDrawer', 'userSettings')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'userDetails')
      }, 300)
    },
    toast (message, level = 'alert-success', timeout = 4000) {
      this.$store.commit('setAlert', { active: true, message, level, timeout })
    },
    // Map Firebase auth error codes to sentences a grower can act on.
    friendlyAuthError (err, context) {
      const code = (err && err.code) || ''
      switch (code) {
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
        case 'auth/invalid-login-credentials':
          return 'That current password is incorrect.'
        case 'auth/too-many-requests':
          return 'Too many attempts. Wait a few minutes and try again, or use the reset link.'
        case 'auth/weak-password':
          return `Choose a stronger password (at least ${MIN_PW} characters).`
        case 'auth/requires-recent-login':
          return 'Please confirm your current password and try again.'
        case 'auth/email-already-in-use':
          return 'That email address is already used by another account.'
        case 'auth/invalid-email':
          return 'That doesn\'t look like a valid email address.'
        case 'auth/network-request-failed':
          return 'Network problem — check your connection and try again.'
        default:
          return (err && err.message) || `Could not update your ${context}.`
      }
    },
    // Confirms the user's identity with their current password. Firebase
    // demands a recent sign-in for sensitive changes; doing this inline is
    // what removes the old "log out and back in first" dead end.
    async reauthenticate (currentPassword) {
      const user = fb.auth.currentUser
      const cred = firebase.auth.EmailAuthProvider.credential(user.email, currentPassword)
      await user.reauthenticateWithCredential(cred)
    },

    // ---------------- profile ----------------
    async updateProfile () {
      this.savingProfile = true
      try {
        await fb.usersCollection.doc(this.currentUser.uid).update({
          firstName: this.profileData.firstName || '',
          lastName: this.profileData.lastName || '',
          company: this.profileData.company || '',
          emailNotifications: this.profileData.emailNotifications !== false,
          emailUpdates: this.profileData.emailUpdates !== false,
          measurementSystem: this.profileData.measurementSystem || 'Imperial'
        })
        this.toast('Profile saved.')
      } catch (err) {
        this.toast(err.message || 'Could not save your profile.', 'alert-warning', 6000)
      } finally {
        this.savingProfile = false
      }
    },

    // ---------------- password ----------------
    validatePassword () {
      this.pwErrors = { current: '', next: '', confirm: '' }
      let ok = true
      if (!this.pw.current) { this.pwErrors.current = 'Enter your current password.'; ok = false }
      if (!this.pw.next) { this.pwErrors.next = 'Enter a new password.'; ok = false } else if (this.pw.next.length < MIN_PW) { this.pwErrors.next = `Use at least ${MIN_PW} characters.`; ok = false } else if (this.pw.next === this.pw.current) { this.pwErrors.next = 'The new password must be different from the current one.'; ok = false }
      if (this.pw.next && this.pw.confirm !== this.pw.next) { this.pwErrors.confirm = 'The two new passwords don\'t match.'; ok = false }
      return ok
    },
    async updatePassword () {
      if (!this.validatePassword()) return
      this.savingPassword = true
      try {
        try {
          await this.reauthenticate(this.pw.current)
        } catch (err) {
          this.pwErrors.current = this.friendlyAuthError(err, 'password')
          return
        }
        await fb.auth.currentUser.updatePassword(this.pw.next)
        this.pw = { current: '', next: '', confirm: '' }
        this.showPw = false
        this.toast('Your password has been updated.')
      } catch (err) {
        const msg = this.friendlyAuthError(err, 'password')
        if ((err && err.code) === 'auth/weak-password') this.pwErrors.next = msg
        else this.toast(msg, 'alert-warning', 6000)
      } finally {
        this.savingPassword = false
      }
    },
    async sendResetLink () {
      const email = fb.auth.currentUser && fb.auth.currentUser.email
      if (!email) return
      try {
        await fb.auth.sendPasswordResetEmail(email)
        this.toast(`We emailed a password reset link to ${email}. Follow it, then sign in with your new password.`, 'alert-info', 8000)
      } catch (err) {
        this.toast(this.friendlyAuthError(err, 'password'), 'alert-warning', 6000)
      }
    },

    // ---------------- email ----------------
    cancelEmailForm () {
      this.showEmailForm = false
      this.em = { next: '', password: '' }
      this.emailErrors = { email: '', password: '' }
    },
    async updateEmail () {
      this.emailErrors = { email: '', password: '' }
      const next = (this.em.next || '').trim()
      if (!next || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) { this.emailErrors.email = 'Enter a valid email address.'; return }
      if (next.toLowerCase() === (this.currentUser.email || '').toLowerCase()) { this.emailErrors.email = 'That is already your email address.'; return }
      if (!this.em.password) { this.emailErrors.password = 'Enter your current password to confirm.'; return }
      this.savingEmail = true
      try {
        try {
          await this.reauthenticate(this.em.password)
        } catch (err) {
          this.emailErrors.password = this.friendlyAuthError(err, 'email')
          return
        }
        const user = fb.auth.currentUser
        try {
          await user.updateEmail(next)
        } catch (err) {
          // Projects with email-enumeration protection require the
          // verify-first flow: Firebase emails the NEW address and switches
          // the account only when that link is clicked.
          if ((err && err.code) === 'auth/operation-not-allowed' && user.verifyBeforeUpdateEmail) {
            await user.verifyBeforeUpdateEmail(next)
            this.toast(`Check ${next} for a confirmation link — your email changes as soon as you click it.`, 'alert-info', 10000)
            this.cancelEmailForm()
            return
          }
          throw err
        }
        await fb.usersCollection.doc(this.currentUser.uid).update({ email: next })
        this.toast('Your email address has been updated.')
        this.cancelEmailForm()
      } catch (err) {
        const msg = this.friendlyAuthError(err, 'email')
        if (['auth/email-already-in-use', 'auth/invalid-email'].includes(err && err.code)) this.emailErrors.email = msg
        else this.toast(msg, 'alert-warning', 6000)
      } finally {
        this.savingEmail = false
      }
    }
  }
}
</script>

<style scoped>
.us-subtitle {
  display: block;
  opacity: 0.8;
  margin-top: 2px;
}
.us-tabs {
  margin: -1.25rem -1.25rem 0;
  background: #fff;
  border-bottom: 1px solid #dee2e6;
}
.us-tabs .nav-tabs {
  border-bottom: none;
}
.us-tabs .nav-link {
  border-radius: 0;
  border: none;
  background: #f1f3f5;
  color: #495057;
  padding: 0.6rem 1rem;
}
.us-tabs .nav-link.active {
  background: #fff;
  color: #212529;
  font-weight: 600;
  box-shadow: inset 0 -3px 0 #ffc107;
}
.us-section {
  padding-top: 0.75rem;
}
.us-h5 {
  margin-bottom: 0.25rem;
}
.us-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 1rem;
}
.us-link {
  display: inline-block;
  margin-top: 4px;
}
</style>
